import { NotificationType } from "components/Notification";
import lodash, { uniq } from "lodash";
import logger from "logger";
import { callUpdateMedicaidVariablesAPI } from "modules/admin/actions";
import {
  EnrichedMedicaidLead,
  MedicaidCalculation,
  MedicaidLeadStatus,
} from "modules/medicaid/types";
import { AnswerValueType, AnswersMap } from "modules/workflow/types/Answer";
import { QuestionnaireID } from "modules/workflow/types/Questionnaire";
import Papa from "papaparse";
import { INVERTED_STATES_MAP, STATES_OPTIONS } from "resources/options/states";

export const updateMedicaidVariables = async (
  questionnaireId: QuestionnaireID,
  answers: AnswersMap,
) => {
  try {
    const response = await callUpdateMedicaidVariablesAPI(
      questionnaireId,
      answers,
    );

    logger.notify(
      NotificationType.SUCCESS,
      "Successfully updated medicaid variables",
    );
    return response;
  } catch (error) {
    logger.notify(
      NotificationType.ERROR,
      undefined,
      "Could not update medicaid variables",
    );
  }
};

enum MedicaidVariablesCSVFieldNames {
  PrePlanningOrCrisis = "Pre Planning Or Crisis",
  HealthInsuranceCost = "Health Insurance Cost",
  PersonalNeedsAllowance = "Personal Needs Allowance",
  UtilityAllowance = "Utility Allowance",
  HousingAllowance = "Housing Allowance",
  TypeOfMMMNAState = "Type of MMMNA State",
  MMMNAMaxValue = "Max Value",
  MMMNAMinValue = "Min Value",
  RegionalDivisorType = "Regional Divisor Type",
  RegionalDivisorValue = "Regional Divisor Value",
  IndividualResourceAllowance = "Individual Resource Allowance",
  SingleIndividualHouseAllowance = "Single Individual House Allowance",
  TypeOfCSRAState = "Type of CSRA State",
  CSRAMaxValue = "Maximum CSRA",
  CSRAMinValue = "Minimum CSRA",
  LifeInsuranceValueBasedOn = "Life Insurance Value based on",
  AmountOfLifeInsuranceExemption = "Amount of life insurance exemption",
  AssetToIncomeRuleExemption = "Asset to income rule exemption",
  IncomeCapState = "Income Cap State",
  IncomeCapAmount = "Income Cap Amount",
  MedicaidDepartmentName = "Name of Dept of Medicaid",
  ISEnabled = "Exempt Retirement Assets IS",
  CSEnabled = "Exempt Retirement Assets CS",
  Jurisdiction = "Jurisdiction",
}

export const fieldNameToMedicaidVariableQuestionId: Record<string, string> = {
  [MedicaidVariablesCSVFieldNames.PrePlanningOrCrisis]:
    "pre-planning-or-crisis",
  [MedicaidVariablesCSVFieldNames.HealthInsuranceCost]:
    "monthly-health-insurance-cost-average",
  [MedicaidVariablesCSVFieldNames.PersonalNeedsAllowance]:
    "personal-needs-allowance",
  [MedicaidVariablesCSVFieldNames.UtilityAllowance]: "utility-allowance",
  [MedicaidVariablesCSVFieldNames.HousingAllowance]: "housing-allowance",
  [MedicaidVariablesCSVFieldNames.TypeOfMMMNAState]: "type-of-mmmna-state",
  [MedicaidVariablesCSVFieldNames.MMMNAMaxValue]: "max-mmmna-value",
  [MedicaidVariablesCSVFieldNames.MMMNAMinValue]: "min-mmmna-value",
  [MedicaidVariablesCSVFieldNames.RegionalDivisorType]: "regional-divisor-type",
  [MedicaidVariablesCSVFieldNames.RegionalDivisorValue]:
    "regional-divisor-value",
  [MedicaidVariablesCSVFieldNames.IndividualResourceAllowance]:
    "individual-resource-allowance",
  [MedicaidVariablesCSVFieldNames.SingleIndividualHouseAllowance]:
    "single-individual-house-allowance",
  [MedicaidVariablesCSVFieldNames.TypeOfCSRAState]: "type-of-csra-state",
  [MedicaidVariablesCSVFieldNames.CSRAMaxValue]: "max-csra-value",
  [MedicaidVariablesCSVFieldNames.CSRAMinValue]: "min-csra-value",
  [MedicaidVariablesCSVFieldNames.LifeInsuranceValueBasedOn]:
    "life-insurance-exemption-based-on",
  [MedicaidVariablesCSVFieldNames.AmountOfLifeInsuranceExemption]:
    "amount-of-allowed-life-insurance-exemption",
  [MedicaidVariablesCSVFieldNames.AssetToIncomeRuleExemption]:
    "asset-to-income-rule-exemption",
  [MedicaidVariablesCSVFieldNames.IncomeCapState]: "income-cap-state",
  [MedicaidVariablesCSVFieldNames.IncomeCapAmount]: "income-cap-amount",
  [MedicaidVariablesCSVFieldNames.MedicaidDepartmentName]:
    "name-of-department-of-medicaid",
  [MedicaidVariablesCSVFieldNames.ISEnabled]: "retirement-assets-exemption-is",
  [MedicaidVariablesCSVFieldNames.CSEnabled]: "retirement-assets-exemption-cs",
};

const mapFieldToAnswerValue = (
  fieldName: string,
  answerString: string,
  jurisdiction: string,
): AnswerValueType => {
  switch (fieldName) {
    case MedicaidVariablesCSVFieldNames.HealthInsuranceCost:
    case MedicaidVariablesCSVFieldNames.PersonalNeedsAllowance:
    case MedicaidVariablesCSVFieldNames.UtilityAllowance:
    case MedicaidVariablesCSVFieldNames.HousingAllowance:
    case MedicaidVariablesCSVFieldNames.MMMNAMaxValue:
    case MedicaidVariablesCSVFieldNames.MMMNAMinValue:
    case MedicaidVariablesCSVFieldNames.RegionalDivisorValue:
    case MedicaidVariablesCSVFieldNames.IndividualResourceAllowance:
    case MedicaidVariablesCSVFieldNames.SingleIndividualHouseAllowance:
    case MedicaidVariablesCSVFieldNames.CSRAMaxValue:
    case MedicaidVariablesCSVFieldNames.CSRAMinValue:
    case MedicaidVariablesCSVFieldNames.IncomeCapAmount:
    case MedicaidVariablesCSVFieldNames.AmountOfLifeInsuranceExemption: {
      const trimmedValue = answerString?.trim();
      if (!trimmedValue) {
        return undefined; // empty value, leave as undefined
      }

      if (!/^\d+(\.\d+)?$/.test(trimmedValue) || Number(trimmedValue) <= 0) {
        throw new Error(
          `Expected a positive numeric value for '${fieldName}' in jurisdiction '${jurisdiction}', but got '${answerString}'.`,
        );
      }
      return Number(trimmedValue);
    }
    case MedicaidVariablesCSVFieldNames.AssetToIncomeRuleExemption:
    case MedicaidVariablesCSVFieldNames.IncomeCapState:
    case MedicaidVariablesCSVFieldNames.ISEnabled:
    case MedicaidVariablesCSVFieldNames.CSEnabled: {
      if (answerString.toLowerCase() === "yes") {
        return true;
      } else if (answerString.toLowerCase() === "no") {
        return false;
      }
      throw Error(
        `Expected either 'Yes' or 'No' for column '${fieldName}' in jurisdiction '${jurisdiction}', but got '${answerString}' instead.`,
      );
    }
    case MedicaidVariablesCSVFieldNames.PrePlanningOrCrisis: {
      if (answerString.toLowerCase() === "pre-planning") {
        return "Pre-planning";
      } else if (answerString.toLowerCase() === "crisis") {
        return "Crisis";
      }
      throw Error(
        `Expected either 'Pre-planning' or 'Crisis' for column '${fieldName}' in jurisdiction '${jurisdiction}', but got '${answerString}' instead.`,
      );
    }
    case MedicaidVariablesCSVFieldNames.TypeOfMMMNAState:
    case MedicaidVariablesCSVFieldNames.TypeOfCSRAState: {
      if (answerString.toLowerCase() === "max") {
        return "Maximum";
      } else if (answerString.toLowerCase() === "range") {
        return "Range";
      }
      throw Error(
        `Expected either 'Max' or 'Range' for column '${fieldName}' in jurisdiction '${jurisdiction}', but got '${answerString}' instead.`,
      );
    }
    case MedicaidVariablesCSVFieldNames.RegionalDivisorType:
      if (answerString.toLowerCase() === "daily") {
        return "Daily";
      } else if (answerString.toLowerCase() === "monthly") {
        return "Monthly";
      }
      throw Error(
        `Expected either 'Daily' or 'Monthly' for column '${fieldName}' in jurisdiction '${jurisdiction}', but got '${answerString}' instead.`,
      );
    case MedicaidVariablesCSVFieldNames.LifeInsuranceValueBasedOn:
      if (answerString.toLowerCase() === "face value") {
        return "Face Value";
      } else if (answerString.toLowerCase() === "cash value") {
        return "Cash Value";
      }
      throw Error(
        `Expected either 'Face Value' or 'Cash Value' for column '${fieldName}' in jurisdiction '${jurisdiction}', but got '${answerString}' instead.`,
      );

    case MedicaidVariablesCSVFieldNames.MedicaidDepartmentName: {
      if (!answerString || answerString.trim() === "") {
        throw new Error(
          `Column '${fieldName}' in jurisdiction '${jurisdiction}' cannot be empty. Please provide a valid value.`,
        );
      }
      return answerString;
    }

    case MedicaidVariablesCSVFieldNames.Jurisdiction: {
      return answerString;
    }
    default: {
      throw Error(
        `Unknown field: '${fieldName}' in jurisdiction '${jurisdiction}'.`,
      );
    }
  }
};

export const parseMedicaidVariablesCSV = (
  file: File,
): Promise<Record<string, AnswersMap>> =>
  new Promise((resolve, reject) => {
    // Check if file type is CSV
    if (!file.name.toLowerCase().endsWith(".csv")) {
      const errorMsg = "Invalid file format. Please upload a CSV file.";
      logger.notify(NotificationType.ERROR, errorMsg);
      return reject(-1);
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          // Filter out rows where only the first column has a value
          const filteredRows = (
            results.data as Record<string, string>[]
          ).filter((row) => {
            const values = Object.values(row);
            const restValues = values.slice(1);
            return !restValues.every((v) => v === "" || v == null);
          });

          const sanitisedMedicaidVariables = lodash.mapKeys(
            lodash.keyBy(
              filteredRows.map((row) => {
                // Validate Jurisdiction exists in INVERTED_STATES_MAP
                const jurisdiction =
                  row[MedicaidVariablesCSVFieldNames.Jurisdiction];
                if (!jurisdiction || !(jurisdiction in INVERTED_STATES_MAP)) {
                  throw new Error(
                    `Invalid Jurisdiction '${jurisdiction}'. Please provide a valid state name.`,
                  );
                }

                const mapped = Object.entries(row)
                  .filter(
                    ([fieldName]) =>
                      fieldName !== MedicaidVariablesCSVFieldNames.Jurisdiction,
                  )
                  .map(([fieldName, answerString]) => ({
                    [fieldNameToMedicaidVariableQuestionId[fieldName]]:
                      mapFieldToAnswerValue(
                        fieldName,
                        answerString,
                        jurisdiction,
                      ),
                  }))
                  .reduce((prev, curr) => ({ ...prev, ...curr }), {});

                return {
                  ...mapped,
                  [MedicaidVariablesCSVFieldNames.Jurisdiction]: jurisdiction,
                };
              }),
              MedicaidVariablesCSVFieldNames.Jurisdiction,
            ),
            (_, jurisdiction) =>
              INVERTED_STATES_MAP[jurisdiction] || jurisdiction,
          );

          // Remove the Jurisdiction grouping key from each answer map
          const cleanedVariables = lodash.mapValues(
            sanitisedMedicaidVariables,
            (answers) => {
              const {
                [MedicaidVariablesCSVFieldNames.Jurisdiction]: _,
                ...rest
              } = answers;
              return rest;
            },
          );

          resolve(cleanedVariables);
        } catch (error: any) {
          logger.notify(
            NotificationType.ERROR,
            error?.message || "Failed to parse Medicaid variables CSV",
          );
          reject(-1);
        }
      },

      error: (error) => {
        logger.notify(
          NotificationType.ERROR,
          error?.message || "Failed to read CSV file",
        );
        reject(-1);
      },
    });
  });

export const getTotalAssetsAtRisk = (calculation: MedicaidCalculation) =>
  calculation.answers.totalAssets;

export const getCurrentlyProtected = (calculation: MedicaidCalculation) =>
  calculation.answers.totalExemptAssets;

export const getTotalAssetsProtected = (calculation: MedicaidCalculation) =>
  calculation.answers.totalExemptAssets +
  calculation.answers.excessAssetsProtected +
  calculation.answers.totalSpendDown;

export const getExcessAssetsProtected = (calculation: MedicaidCalculation) =>
  calculation.answers.excessAssetsProtected;

export const getAdditionalMonthlyProtection = (
  calculation: MedicaidCalculation,
) => calculation.answers.monthlyDeficit;

export const getFullMonthsToQualify = (calculation: MedicaidCalculation) =>
  Math.ceil(calculation.answers.minimumMonthsToQualify);

export const getTotalNotSpentOnNHCare = (calculation: MedicaidCalculation) =>
  calculation.answers.totalNotSpentOnNHCare;

export const getTotalProtectedOnceQualified = (
  calculation: MedicaidCalculation,
) =>
  getTotalAssetsProtected(calculation) +
  getFullMonthsToQualify(calculation) *
    getAdditionalMonthlyProtection(calculation);

/** CSV column order for template generation (excludes Jurisdiction which is always first). */
const CSV_VARIABLE_COLUMNS = [
  MedicaidVariablesCSVFieldNames.PrePlanningOrCrisis,
  MedicaidVariablesCSVFieldNames.HealthInsuranceCost,
  MedicaidVariablesCSVFieldNames.PersonalNeedsAllowance,
  MedicaidVariablesCSVFieldNames.UtilityAllowance,
  MedicaidVariablesCSVFieldNames.HousingAllowance,
  MedicaidVariablesCSVFieldNames.TypeOfMMMNAState,
  MedicaidVariablesCSVFieldNames.MMMNAMaxValue,
  MedicaidVariablesCSVFieldNames.MMMNAMinValue,
  MedicaidVariablesCSVFieldNames.RegionalDivisorType,
  MedicaidVariablesCSVFieldNames.RegionalDivisorValue,
  MedicaidVariablesCSVFieldNames.IndividualResourceAllowance,
  MedicaidVariablesCSVFieldNames.SingleIndividualHouseAllowance,
  MedicaidVariablesCSVFieldNames.TypeOfCSRAState,
  MedicaidVariablesCSVFieldNames.CSRAMaxValue,
  MedicaidVariablesCSVFieldNames.CSRAMinValue,
  MedicaidVariablesCSVFieldNames.LifeInsuranceValueBasedOn,
  MedicaidVariablesCSVFieldNames.AmountOfLifeInsuranceExemption,
  MedicaidVariablesCSVFieldNames.AssetToIncomeRuleExemption,
  MedicaidVariablesCSVFieldNames.IncomeCapState,
  MedicaidVariablesCSVFieldNames.IncomeCapAmount,
  MedicaidVariablesCSVFieldNames.MedicaidDepartmentName,
  MedicaidVariablesCSVFieldNames.ISEnabled,
  MedicaidVariablesCSVFieldNames.CSEnabled,
];

/**
 * Generates a CSV template blob with all Medicaid variable columns and one row
 * per US state. Variable cells are left empty for the user to fill in.
 */
export const generateMedicaidVariablesCSVTemplate = (): Blob => {
  const headers = [
    MedicaidVariablesCSVFieldNames.Jurisdiction,
    ...CSV_VARIABLE_COLUMNS,
  ];

  // Header description row (matches existing CSV format convention — row 0 is descriptions, row 1+ is data)
  const descriptionRow = headers.map(() => "");

  const dataRows = STATES_OPTIONS.map(({ label }) => {
    const row: string[] = [label];
    CSV_VARIABLE_COLUMNS.forEach(() => row.push(""));
    return row;
  });

  const csvContent = Papa.unparse({
    fields: headers,
    data: [descriptionRow, ...dataRows],
  });

  return new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
};
export const ALL_STATUSES = "All Statuses";
export const STATUS_OPTIONS = [
  { label: ALL_STATUSES, value: ALL_STATUSES },
  ...uniq(Object.values(MedicaidLeadStatus)).map((value) => ({
    label: value.replace(/([A-Z])/g, " $1").trim(),
    value,
  })),
];

export const filterStatus = (
  selectedStatus: MedicaidLeadStatus | typeof ALL_STATUSES,
  enrichedMedicaidLead: EnrichedMedicaidLead,
) => {
  if (selectedStatus === ALL_STATUSES) return true;
  return enrichedMedicaidLead.medicaidLead.status === selectedStatus;
};
